import { type FC, useEffect, useRef, useState, type CSSProperties } from 'react';
import classnames from 'classnames';
import VideoFlatIcon from 'dibs-icons/exports/legacy/VideoFlat';
import Icon3dIndicator from 'dibs-icons/exports/legacy/Icon3dIndicator';
import { VideoTile } from 'dibs-elements/exports/VideoTile';
import { Spinner } from 'dibs-elements/exports/Spinner';

import { ioParamsSubset, ioParams } from 'dibs-fastly/exports/imageOptimizations';
import { colors } from 'dibs-sassy/exports/colors';
import { NftItemTileMediaUnavailable } from './NftItemTileMediaUnavailable';

import styles from './styles/NftItemTileMedia.scss';

const POSTER_EXCEPTION_ITEM_IDS = ['n_c5NBn5eAAw', 'n_3AK6fqzHOH', 'n_lwnIqkpdUl', 'n_Ommj6G9IVC'];

/**
 * @see https://1stdibs.atlassian.net/browse/NFTS-289
 * @see https://1stdibs.atlassian.net/browse/NFTS-315
 */
const getPosterOverride = (
    itemId: string | null | undefined,
    posterSrc: string
): string | undefined => (POSTER_EXCEPTION_ITEM_IDS.includes(itemId || '') ? posterSrc : undefined);

// media is 339px wide on iphoneX; all other sizes should be smaller
// after further reading looks like the iphone 12 pro max is 428px wide with a
// 3x dpr and the iphone 12 is 390px W with 3x dpr...
const MAX_MEDIA_SIZE = '339';

const baseArgs = ioParamsSubset({
    height: MAX_MEDIA_SIZE,
    width: MAX_MEDIA_SIZE,
    precrop: '1:1',
    // slightly reduces size of contrast-heavy img without introducing pixelation
    'resize-filter': 'bilinear',
    // match site bg color when alpha is replaced. this is noticeable when gifs
    // with alpha are cropped and converted to mp4.
    'bg-color': colors.sassyColorWhite.replace('#', ''),
});

// force png to jpg (this has a huge impact in decreasing size)
const imgParams = ioParams(baseArgs, { format: 'pjpg' });

const staticImgString = ioParams(imgParams, {
    dpr: '2',
    auto: 'webp', // webp if available (better optimization than jpg)
    optimize: 'high', // video posters look a little off no matter what, nothing else is noticeable
}).toQuery();

const imgPosterString = ioParams(imgParams, { auto: 'webp', optimize: 'high' }).toQuery();

const gifPosterString = ioParams(imgParams, {
    frame: '1', // use first frame of gif for poster
}).toQuery();

const gifToMp4String = ioParams(baseArgs, {
    format: 'mp4', // convert gif to mp4
}).toQuery();

export const NftTileMedia: FC<{
    animate: boolean;
    mimeType?: string | null;
    webUrl?: string | null;
    videoThumb?: string | null;
    posterFrameUrl?: string | null;
    altText?: string | null;
    posterOverrideId?: string | null;
    showIcon?: boolean;
    isTransparent?: boolean;
    showSpinner?: boolean;
}> = ({
    animate,
    mimeType,
    webUrl,
    videoThumb,
    posterFrameUrl,
    altText,
    posterOverrideId,
    showIcon,
    isTransparent,
    showSpinner,
}) => {
    const [isLoading, setIsLoading] = useState(true);
    const imageRef = useRef<HTMLImageElement>(null);

    // onLoad event does not trigger if image is loaded from browser cache
    useEffect(() => {
        if (imageRef.current?.complete) {
            setIsLoading(false);
        }
    }, []);

    const is3dModel = mimeType?.includes('model');
    const isVideo = mimeType?.startsWith('video');
    const isGif = mimeType === 'image/gif';
    const isHtml = mimeType === 'text/html';

    let mediaComponent;
    let poster;
    if (isVideo) {
        poster = posterFrameUrl + imgPosterString;
        mediaComponent = (
            <VideoTile
                posterOverride={getPosterOverride(posterOverrideId, poster)}
                sourceUrl={videoThumb || webUrl || ''}
                animate={animate}
            />
        );
    } else if (isGif) {
        poster = posterFrameUrl ? posterFrameUrl + imgPosterString : webUrl + gifPosterString;
        mediaComponent = (
            <VideoTile
                posterOverride={getPosterOverride(posterOverrideId, poster)}
                sourceUrl={webUrl + gifToMp4String}
                animate={animate}
            />
        );
    } else if (is3dModel || isHtml) {
        mediaComponent = (
            <img
                loading="lazy"
                ref={imageRef}
                onLoad={() => setIsLoading(false)}
                src={posterFrameUrl + staticImgString}
                alt={altText || ''}
                className={classnames(styles.media, { [styles.loadingShimmer]: isLoading })}
            />
        );
    } else {
        mediaComponent = (
            <img
                loading="lazy"
                ref={imageRef}
                onLoad={() => setIsLoading(false)}
                src={webUrl + staticImgString}
                alt={altText || ''}
                className={classnames(styles.media, { [styles.loadingShimmer]: isLoading })}
            />
        );
    }

    if (!webUrl) {
        mediaComponent = <NftItemTileMediaUnavailable showSpinner={showSpinner} />;
    }

    return (
        <div className={styles.mediaContainer}>
            {isVideo && showIcon ? <VideoFlatIcon className={styles.videoIcon} /> : null}
            {is3dModel && showIcon ? <Icon3dIndicator className={styles.icon3DIndicator} /> : null}

            <div className={styles.squareMediaArea} />
            <div
                className={classnames(styles.mediaWrapper, {
                    [styles.transparent]: isTransparent || showSpinner,
                })}
                style={{ ['--poster']: (poster && `url(${poster})`) || undefined } as CSSProperties}
            >
                {mediaComponent}
            </div>
            {showSpinner && webUrl && <Spinner containerClass={styles.spinner} black />}
        </div>
    );
};
